/**
 * @fileoverview Isolated internal note form to prevent re-render of the inbox when updating messages.
 * Keeps the textarea state stable while the selected conversation does not change id.
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useRef
} from 'react';
import FormProviderMC from '../../../components/FormProviderMC';
import ButtonComponent from '../../../components/ButtonMC';
import useChatNoteForm from '../../../hooks/inbox/forms/useChatNoteForm';
import useCompleteMessageActions from '../../../hooks/inbox/actions/useCompleteMessageActions';
import MessageCounterMC from './MessageCounterMC';
import InputRefMC from './InputRefMC';
import { sharingInformationService } from '../../../../services/sharingInformationService';

const IsolatedChatNoteFormMC = memo(({
  conversationId,
  getConversationMeta,
  addNoteLabel,
  textAreaRef
}) => {
  const {
    methods,
    resetForm,
    maxChars,
    placeholder
  } = useChatNoteForm({ conversationId });

  const { sendAndAddMessage } = useCompleteMessageActions();
  const getConversationMetaRef = useRef(getConversationMeta);
  getConversationMetaRef.current = getConversationMeta;

  useEffect(() => {
    textAreaRef?.current?.focus();
  }, [conversationId, textAreaRef]);

  const clearNoteInput = useCallback(() => {
    resetForm();
    if (textAreaRef?.current) {
      textAreaRef.current.value = '';
      textAreaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, [resetForm, textAreaRef]);

  const handleSubmit = useCallback(() => {
    methods.handleSubmit((data) => {
      const meta = getConversationMetaRef.current?.() || {};
      sendAndAddMessage({
        ...data,
        ...meta
      });
      clearNoteInput();
      setTimeout(() => {
        sharingInformationService.setSubject({ refreshListDataNote: true });
        sharingInformationService.setSubject({});
      }, 2000);
    })();
  }, [methods, sendAndAddMessage, clearNoteInput]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  return (
    <FormProviderMC methods={methods}>
      <div className="tw-px-4 tw-w-full tw-mb-4 tw-flex-1 tw-min-w-0">
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-p-1 tw-flex tw-flex-col tw-gap-1 tw-items-stretch tw-w-full tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-w-full tw-items-center tw-gap-2">
            <InputRefMC
              textarea
              hideBorder
              ref={textAreaRef}
              maxLength={maxChars}
              name="message"
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              containerClass="tw-flex-1 tw-min-w-0"
              className="tw-bg-white tw-flex-1 tw-min-w-0 tw-resize-none tw-placeholder-gray-400 focus:tw-ring-0 focus:tw-border-transparent focus:tw-shadow-none focus:tw-outline-none focus:tw-ring-transparent focus:tw-border-0 !tw-py-0"
            />
          </div>
          <MessageCounterMC textareaRef={textAreaRef} maxChars={maxChars} />
          <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
            <ButtonComponent
              onClick={handleSubmit}
              text={addNoteLabel}
              size="medium"
              className="tw-text-xs"
            />
          </div>
        </div>
      </div>
    </FormProviderMC>
  );
}, (prevProps, nextProps) => (
  prevProps.conversationId === nextProps.conversationId
  && prevProps.addNoteLabel === nextProps.addNoteLabel
  && prevProps.getConversationMeta === nextProps.getConversationMeta
));

IsolatedChatNoteFormMC.displayName = 'IsolatedChatNoteFormMC';

export default IsolatedChatNoteFormMC;
